import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import TypingText from '../UI/TypingText';

const SceneContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  color: #fff;
  padding: 2rem;
  box-sizing: border-box;
`;

const HolographicInterface = styled.div`
  position: relative;
  width: 600px;
  height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    width: 350px;
    height: 350px;
  }
`;

const CentralCore = styled.div`
  width: 200px;
  height: 200px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: 
      conic-gradient(from 0deg, #00ff88, #00cc6a, #44ff99, #00ff88);
    border-radius: 50%;
    animation: rotate 8s linear infinite;
    filter: blur(2px);
    opacity: 0.8;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 180px;
    height: 180px;
    background: radial-gradient(circle, rgba(0, 255, 136, 0.1), rgba(0, 0, 0, 0.9));
    border: 2px solid #00ff88;
    border-radius: 50%;
    box-shadow: 
      0 0 50px rgba(0, 255, 136, 0.5),
      inset 0 0 30px rgba(0, 255, 136, 0.2);
    animation: pulse 3s ease-in-out infinite;
  }
  
  @keyframes rotate {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); box-shadow: 0 0 50px rgba(0, 255, 136, 0.5), inset 0 0 30px rgba(0, 255, 136, 0.2); }
    50% { transform: scale(1.05); box-shadow: 0 0 80px rgba(0, 255, 136, 0.8), inset 0 0 50px rgba(0, 255, 136, 0.4); }
  }
`;

const GeometricRings = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  
  .ring {
    position: absolute;
    border: 1px solid rgba(0, 255, 136, 0.3);
    border-radius: 50%;
    
    &.ring-1 {
      width: 300px;
      height: 300px;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      animation: rotate-reverse 15s linear infinite;
    }
    
    &.ring-2 {
      width: 400px;
      height: 400px;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      animation: rotate 12s linear infinite;
      border-style: dashed;
    }
    
    &.ring-3 {
      width: 500px;
      height: 500px;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      animation: rotate-reverse 20s linear infinite;
      opacity: 0.5;
    }
  }
  
  @keyframes rotate-reverse {
    0% { transform: translate(-50%, -50%) rotate(360deg); }
    100% { transform: translate(-50%, -50%) rotate(0deg); }
  }
`;

const EnergyNodes = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  
  .node {
    position: absolute;
    width: 12px;
    height: 12px;
    background: radial-gradient(circle, #00ff88, transparent);
    border: 1px solid #00ff88;
    border-radius: 50%;
    animation: node-pulse 2s ease-in-out infinite;
    
    &::before {
      content: '';
      position: absolute;
      top: -4px;
      left: -4px;
      width: 18px;
      height: 18px;
      border: 1px solid rgba(0, 255, 136, 0.3);
      border-radius: 50%;
      animation: node-expand 2s ease-in-out infinite;
    }
    
    &.node-1 {
      top: 10%;
      left: 50%;
      transform: translateX(-50%);
      animation-delay: 0s;
    }
    
    &.node-2 {
      top: 30%;
      right: 15%;
      animation-delay: 0.3s;
    }
    
    &.node-3 {
      top: 70%;
      right: 15%;
      animation-delay: 0.6s;
    }
    
    &.node-4 {
      bottom: 10%;
      left: 50%;
      transform: translateX(-50%);
      animation-delay: 0.9s;
    }
    
    &.node-5 {
      top: 70%;
      left: 15%;
      animation-delay: 1.2s;
    }
    
    &.node-6 {
      top: 30%;
      left: 15%;
      animation-delay: 1.5s;
    }
  }
  
  @keyframes node-pulse {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }
  
  @keyframes node-expand {
    0%, 100% { transform: scale(1); opacity: 0.3; }
    50% { transform: scale(1.5); opacity: 0.1; }
  }
`;

const CoreSymbol = styled.div`
  position: relative;
  z-index: 10;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: 300;
  color: #00ff88;
  text-shadow: 0 0 20px #00ff88;
  
  &::before {
    content: '◊';
    position: absolute;
    font-size: 3rem;
    animation: symbol-rotate 6s ease-in-out infinite;
  }
  
  @keyframes symbol-rotate {
    0%, 100% { transform: rotate(0deg) scale(1); }
    50% { transform: rotate(180deg) scale(1.2); }
  }
`;

const MainTitle = styled.h1`
  font-size: 4rem;
  font-weight: 100;
  text-transform: uppercase;
  letter-spacing: 8px;
  margin: 0 0 1rem 0;
  text-align: center;
  background: linear-gradient(135deg, #ffffff 0%, #00ff88 50%, #44ff99 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 200px;
    height: 2px;
    background: linear-gradient(90deg, transparent, #00ff88, transparent);
    animation: line-glow 3s ease-in-out infinite;
  }
  
  @keyframes line-glow {
    0%, 100% { opacity: 0.5; width: 200px; }
    50% { opacity: 1; width: 300px; }
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    letter-spacing: 4px;
  }
`;

const SubTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 200;
  color: #00cc6a;
  text-align: center;
  margin: 0 0 2rem 0;
  letter-spacing: 4px;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    letter-spacing: 2px;
  }
`;

const WelcomeText = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  max-width: 600px;
  line-height: 1.6;
  letter-spacing: 1px;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    max-width: 90%;
  }
`;

const EnterPrompt = styled.div`
  position: absolute;
  bottom: 5rem;
  left: 50%;
  transform: translateX(-50%);
  color: #00ff88;
  font-size: 1rem;
  font-weight: 300;
  letter-spacing: 2px;
  animation: fade-pulse 2s ease-in-out infinite;
  
  &::after {
    content: '↓';
    display: block;
    text-align: center;
    font-size: 1.5rem;
    margin-top: 0.5rem;
    animation: bounce 1.5s ease-in-out infinite;
  }
  
  @keyframes fade-pulse {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }
  
  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(5px); }
  }
`;

const HallScene: React.FC = () => {
  const sceneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    // Animate the holographic interface elements
    gsap.timeline()
      .fromTo('.ring', 
        { scale: 0, opacity: 0 },
        { scale: 1, opacity: 1, duration: 1.5, ease: "power2.out", stagger: 0.2 }
      )
      .fromTo('.node', 
        { scale: 0, opacity: 0 },
        { scale: 1, opacity: 1, duration: 1, ease: "back.out(1.7)", stagger: 0.1 },
        "-=1"
      )
      .fromTo('.central-core', 
        { scale: 0, opacity: 0 },
        { scale: 1, opacity: 1, duration: 1.5, ease: "power2.out" },
        "-=0.8"
      );

    // Animate text elements
    gsap.timeline({ delay: 0.5 })
      .fromTo('.main-title', 
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 1.2, ease: "power2.out" }
      )
      .fromTo('.sub-title', 
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: "power2.out" },
        "-=0.8"
      )
      .fromTo('.welcome-text', 
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: "power2.out" },
        "-=0.6"
      )
      .fromTo('.enter-prompt', 
        { opacity: 0 },
        { opacity: 1, duration: 0.5 },
        "+=0.5"
      );

  }, []);

  return (
    <SceneContainer ref={sceneRef} className="scene">
      <HolographicInterface className="holographic-interface">
        <GeometricRings>
          <div className="ring ring-1"></div>
          <div className="ring ring-2"></div>
          <div className="ring ring-3"></div>
        </GeometricRings>
        
        <EnergyNodes>
          <div className="node node-1"></div>
          <div className="node node-2"></div>
          <div className="node node-3"></div>
          <div className="node node-4"></div>
          <div className="node node-5"></div>
          <div className="node node-6"></div>
        </EnergyNodes>
        
        <CentralCore className="central-core">
          <CoreSymbol />
        </CentralCore>
      </HolographicInterface>
      
      <MainTitle className="main-title">
        <TypingText 
          text="ZERO LIMITS" 
          speed={150} 
          delay={1000}
        />
      </MainTitle>
      <SubTitle className="sub-title">
        <TypingText 
          text="Hall of Innovation" 
          speed={100} 
          delay={2500}
        />
      </SubTitle>
      <WelcomeText className="welcome-text">
        <TypingText 
          text="Enter a realm where boundaries dissolve and possibilities expand infinitely. Your journey beyond conventional limits begins here." 
          speed={50} 
          delay={4000}
          cursor={false}
        />
      </WelcomeText>
      
      <EnterPrompt className="enter-prompt">
        <TypingText 
          text="SCROLL TO EXPLORE ↓" 
          speed={80} 
          delay={7000}
        />
      </EnterPrompt>
    </SceneContainer>
  );
};

export default HallScene;
